/**
 * author-form-section block
 *
 * Dark two-column section: a light-weight display heading (and optional
 * copy / author photo + bio) on the left, a white rounded form card on the
 * right. Mirrors the "A methodology you run Monday" section on the Agentic
 * Adaptation Playbook page.
 *
 * The form renders a HubSpot embed when a `form.form_id` (+ `portal_id`) is
 * provided. Without one it shows a clearly-labelled placeholder so the
 * layout previews without wiring a wrong (lead-misrouting) form. HubSpot
 * form IDs are marketing-owned — supply the real one to go live.
 *
 * Omit `form` entirely and the section becomes copy (+ optional `button`) on
 * the left and `bg_image` on the right, with no form card. In that shape the
 * right-half backdrop is the only thing in the second column, so the image
 * also renders in flow below lg — where that backdrop is hidden and the form
 * card is no longer there to fill the space.
 *
 * That photo shape has two modifiers, both lg+ only and both ignored when a
 * `form` is present (the form owns the second column):
 *
 * - `image_first` mirrors the section — photo half on the left, copy on the
 *   right. The stacked order below lg is deliberately NOT flipped: heading
 *   then photo reads the same on a phone for every blade, and flipping it
 *   would put two photos back to back when these sections are stacked.
 * - `image_position` chooses which part of the photo survives the crop. The
 *   panel always fills the section top to bottom, so its aspect is whatever
 *   the copy column's height makes it and the photo is cropped to fit — it is
 *   object-cover throughout, never stretched.
 *
 * The `photo` inset under the copy has one of its own: `photo_sharp`, which
 * squares off its corners.
 */
import { Button } from "~/components/Button";
import { Image } from "~/components/Image";
import { HubSpotForm } from "~/components/forms/HubSpotForm";

export function AuthorFormSection({
  id,
  eyebrow,
  heading,
  copy,
  button,
  photo,
  photo_alt,
  photo_sharp,
  name,
  bio,
  form,
  flush_bottom,
  light,
  bg_image,
  bg_image_alt,
  image_first,
  image_position,
}: {
  /** Anchor id so in-page links (e.g. a CTA) can scroll to this section. */
  id?: string;
  /** Remove the section's bottom padding so it sits flush against the footer. */
  flush_bottom?: boolean;
  /** Light theme: white section background with dark text and no dark split
   *  panel behind the form. Opt-in; defaults to the dark two-column look. */
  light?: boolean;
  /** Image shown behind the form (right half of the section, lg+). Replaces
   *  the plain split panel. Opt-in. */
  bg_image?: string;
  bg_image_alt?: string;
  /** Mirror the photo shape: image half on the left (lg+), copy on the right.
   *  No effect with a `form`, which owns the second column. Opt-in. */
  image_first?: boolean;
  /** object-position for the image half — which part of the photo survives the
   *  crop. Defaults to center. */
  image_position?: string;
  /** Small uppercase label above the heading. One string or several lines.
   *  Matches the eyebrow typography used on the alternating card sections. */
  eyebrow?: string | string[];
  /** Section heading. Supports <br/>. */
  heading?: string;
  copy?: string;
  /** Optional CTA under the copy, for sending visitors to a page rather than
   *  to the form beside it (e.g. registration). Opt-in. */
  button?: { text: string; url: string; target?: string; rel?: string };
  photo?: string;
  photo_alt?: string;
  /** Square off the photo inset's corners instead of rounding them. Opt-in. */
  photo_sharp?: boolean;
  name?: string;
  bio?: string;
  form?: {
    portal_id?: string;
    form_id?: string;
    heading?: string;
    /** Optional supporting line under the form heading. */
    subhead?: string;
    cta?: string;
  };
}) {
  // Mirroring only applies to the photo shape. With a form card the second
  // column is the form, so there is no image half to move.
  const imageLeft = Boolean(bg_image && !form && image_first);
  // Which half of the section the backdrop covers.
  const backdropSide = imageLeft ? "left-0 right-1/2" : "left-1/2 right-0";
  return (
    <section
      id={id}
      className={`relative isolate scroll-mt-24 px-4 pt-16 md:px-8 md:pt-20 ${
        light ? "bg-white text-charcoal" : "bg-dark text-white"
      } ${flush_bottom ? "pb-0" : "pb-16 md:pb-20"}`}
    >
      {/* Backdrop beside the copy — the right half of the section by default,
          the left half with image_first. A photo when bg_image is set,
          otherwise the dark-theme split panel. */}
      {bg_image ? (
        <div
          aria-hidden={bg_image_alt ? undefined : true}
          className={`pointer-events-none absolute inset-y-0 z-0 hidden overflow-hidden lg:block ${backdropSide}`}
        >
          <Image
            src={bg_image}
            alt={bg_image_alt || ""}
            className="h-full w-full object-cover"
            style={image_position ? { objectPosition: image_position } : undefined}
            loading="lazy"
            sizes="50vw"
          />
        </div>
      ) : (
        !light && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 z-0 hidden bg-black lg:block ${backdropSide}`}
          />
        )
      )}
      <div className="relative z-10 mx-auto max-w-screen-xl">
        {/* With a form card the second column is sized to it. Without one the
            columns split evenly, so the copy stops at the section's midpoint —
            where the right-half backdrop begins. */}
        <div
          className={`grid gap-10 lg:items-start lg:gap-14 ${
            form ? "lg:grid-cols-[1fr_minmax(0,440px)]" : "lg:grid-cols-2"
          }`}
        >
          {/* Heading, copy, optional author photo + bio. Left half by
              default; the right one (second grid column) with image_first. */}
          <div className={imageLeft ? "lg:col-start-2 lg:pl-14" : "lg:pr-14"}>
            {eyebrow && (
              <div className="mb-6 space-y-0.5">
                {(Array.isArray(eyebrow) ? eyebrow : [eyebrow]).map((line, k) => (
                  <p
                    key={k}
                    className={`font-body font-semibold text-sm md:text-base uppercase tracking-normal ${
                      light ? "text-charcoal" : "text-white"
                    }`}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
            {heading && (
              <h2
                className="font-display text-[50.5px] font-semibold leading-[1.1]"
                dangerouslySetInnerHTML={{ __html: heading }}
              />
            )}
            {copy && (
              <p
                className={`mt-6 max-w-xl text-19 leading-relaxed ${
                  light ? "text-charcoal/80" : "text-white/78"
                }`}
              >
                {copy}
              </p>
            )}
            {button && (
              <div className="mt-8">
                <Button
                  href={button.url}
                  variant="teal"
                  size="lg"
                  target={button.target}
                  rel={button.rel}
                >
                  <span>{button.text}</span>
                </Button>
              </div>
            )}
            {/* Photo + caption sit side by side, bottom-aligned. Below xs the
                column is too narrow to give the caption a readable measure
                beside a 216px photo (~87px at 390px wide), so it stacks. */}
            {(photo || name || bio) && (
              <div className="mt-10 flex flex-col items-start gap-4 xs:flex-row xs:items-end xs:gap-6">
                {photo && (
                  <img
                    src={photo}
                    alt={photo_alt || ""}
                    className={`h-auto w-[216px] shrink-0 object-cover md:w-[264px] lg:h-[365px] ${
                      photo_sharp ? "" : "rounded-lg"
                    }`}
                    loading="lazy"
                  />
                )}
                {(name || bio) && (
                  <div>
                    {name && (
                      <p
                        className={`font-display text-19 font-semibold leading-snug md:text-23 ${
                          light ? "text-charcoal" : "text-white"
                        }`}
                      >
                        {name}
                      </p>
                    )}
                    {bio && (
                      <p
                        className={`mt-2 max-w-[300px] text-[17px] leading-relaxed ${
                          light ? "text-charcoal/80" : "text-white/84"
                        }`}
                      >
                        {bio}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Below lg only: the backdrop image in flow. Without a form card the
              second column would otherwise be empty on small screens, since
              the split backdrop is lg+ only. Stays after the copy even with
              image_first — see the note at the top of this file. */}
          {bg_image && !form && (
            <div className="overflow-hidden rounded-xl lg:hidden">
              <Image
                src={bg_image}
                alt={bg_image_alt || ""}
                className="h-full w-full object-cover"
                loading="lazy"
                sizes="100vw"
              />
            </div>
          )}

          {/* Right: form card */}
          {form && (
            <div className="hs-form-teal relative rounded-xl border border-gray-5 bg-white p-5 pb-9 text-charcoal shadow-card md:p-10">
              {form.heading && (
                <p className={`${form.subhead ? "mb-2" : "mb-6"} font-display text-2xl font-medium text-charcoal`}>
                  {form.heading}
                </p>
              )}
              {form.subhead && (
                <p className="mb-6 text-[15px] leading-snug text-gray-80">
                  {form.subhead}
                </p>
              )}
              {form.form_id ? (
                <HubSpotForm
                  formId={form.form_id}
                  portalId={form.portal_id}
                  formCta={form.cta}
                  placeholdersFromLabels
                />
              ) : (
                <div className="space-y-3">
                  <div className="h-11 rounded-md border border-gray-5 bg-gray-2/60" />
                  <div className="h-11 rounded-md border border-gray-5 bg-gray-2/60" />
                  <div className="h-11 rounded-md border border-gray-5 bg-gray-2/60" />
                  <div className="mt-2 h-11 rounded-md bg-teal-dark" />
                  <p className="pt-1 text-center text-xs text-gray-80">
                    Form placeholder — add the HubSpot portal &amp; form ID to go live.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
